/**
 * billingSnapshotCache — ONE answer to "what is THIS viewer's billing
 * situation, in THIS workspace?", shared by /pricing and /subscription.
 *
 * SPEED + TRUTH (owner report 2026-08-25: "the subscription and pricing pages
 * need to load much much faster — the user sees one thing and it takes
 * several seconds for them to see conflicting information").
 *
 * Two defects produced that experience, and this module fixes both:
 *
 *  1. SLOW — resolution was a five-hop SERIAL waterfall: three awaited dynamic
 *     imports (each a chunk fetch on first use), then the workspace-options
 *     callable, then a wait-for-auth token (up to AUTH_WAIT_MS), then the
 *     status call. Nothing downstream of the imports actually depended on
 *     them finishing one at a time. Here the imports, the workspace options
 *     and the auth token all resolve CONCURRENTLY, leaving one dependent hop:
 *     the subscription read itself.
 *
 *  2. CONFLICTING — every visit re-derived the answer from nothing, so both
 *     pages painted a guess (the hire pitch, or the caller's PERSONAL plan)
 *     and then contradicted it seconds later with the real answer. The last
 *     verified snapshot is now PERSISTED per-uid and served synchronously, so
 *     a repeat visit paints the REAL answer in the first frame and the
 *     background refresh is invisible unless the facts genuinely changed.
 *
 * Persistence discipline is `workspaceOptionsCache`'s, deliberately: a
 * versioned, uid-keyed, shape-validated, age-bounded copy of a list the
 * SERVER verified for this exact uid. It is never trusted across users, so
 * switching accounts cannot leak another viewer's billing state. It is a
 * PAINT accelerator only — every write the pages perform still goes to the
 * server, and `invalidate()` drops it the moment a plan changes.
 *
 * Leaf-module rule: no static firebase imports (jsdom-testable in isolation);
 * the firebase surfaces load dynamically inside the resolver.
 */
import type { Auth, User } from 'firebase/auth';
import type { HireCadence } from '../config/workUnits';

/**
 * The discriminated billing state both pages render from.
 *
 *  - offer  — no live wu_* plan and the viewer may buy
 *  - plan   — live wu_* plan the viewer may see; canManage gates the adjuster
 *  - view   — org member with billing.view only (read-only + requests)
 *  - wall   — org member with no billing permission at all
 */
export interface BillingSnapshot {
  kind: 'offer' | 'plan' | 'view' | 'wall';
  scope: 'personal' | 'org';
  /** Org target for subscribeDirect/changeSubscription; absent on personal. */
  accountId?: string;
  /** Human label for the workspace ("your workspace" on personal). */
  label: string;
  /** plan/view states with a subscription: */
  plan?: string;
  seats?: number;
  term?: HireCadence;
  currentPeriodEnd?: number | null;
  isTrial?: boolean;
  canManage?: boolean;
  /**
   * 'internal-grant' when the live plan is provided by an internal paid grant
   * (2026-09-15): a paid posture with no Stripe subscription behind it, so the
   * pricing panel shows the plan and offers nothing to apply.
   */
  entitlementSource?: string | null;
}

const PERSIST_KEY = 'prest0_billing_snapshot_v1';
/**
 * Persisted copies older than this never paint. Billing state changes rarely
 * and every change invalidates, so the bound is hygiene — not freshness.
 */
const PERSIST_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Memory copies inside this window skip revalidation entirely. */
const MEMORY_TTL_MS = 60_000;
/**
 * Ceiling on the whole resolution. The old code could sit on the auth wait
 * alone for 4s; past this the caller keeps whatever it is already painting
 * rather than holding a skeleton open indefinitely.
 */
const RESOLVE_TIMEOUT_MS = 8_000;

const LIVE_STATUSES = ['active', 'trialing', 'past_due'];

let memory: { uid: string; at: number; snapshot: BillingSnapshot } | null = null;
let inflight: { uid: string; promise: Promise<BillingSnapshot | null> } | null = null;

const FN_BASE = (() => {
  const projectId =
    process.env.REACT_APP_FIREBASE_PROJECT_ID || 'project-olivia-415';
  return `https://us-central1-${projectId}.cloudfunctions.net`;
})();

export function fnUrl(name: string): string {
  return `${FN_BASE}/${name}`;
}

/** wu_* plan id → the adjuster's cadence seed. */
export function termFromPlan(plan: string): HireCadence {
  if (plan === 'wu_monthly') return 'monthly';
  if (plan.startsWith('wu_prepay')) return 'prepay';
  return 'annual';
}

function isSnapshotShape(o: unknown): o is BillingSnapshot {
  const r = o as Record<string, unknown> | null;
  if (!r) return false;
  if (r.kind !== 'offer' && r.kind !== 'plan' && r.kind !== 'view' && r.kind !== 'wall') return false;
  if (r.scope !== 'personal' && r.scope !== 'org') return false;
  return typeof r.label === 'string';
}

/**
 * True when two snapshots say the same thing. Guards the background refresh
 * from re-rendering (and re-flashing) a page whose facts did not change.
 */
export function sameSnapshot(a: BillingSnapshot | null, b: BillingSnapshot | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.kind === b.kind
    && a.scope === b.scope
    && a.accountId === b.accountId
    && a.label === b.label
    && a.plan === b.plan
    && a.seats === b.seats
    && a.term === b.term
    && a.currentPeriodEnd === b.currentPeriodEnd
    && a.isTrial === b.isTrial
    && a.canManage === b.canManage
    && (a.entitlementSource ?? null) === (b.entitlementSource ?? null);
}

function keyFor(uid: string): string {
  return `${PERSIST_KEY}:${uid}`;
}

function readPersisted(uid: string): BillingSnapshot | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(keyFor(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v?: number; uid?: string; at?: number; snapshot?: unknown };
    if (parsed.v !== 1 || parsed.uid !== uid) return null;
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > PERSIST_MAX_AGE_MS) return null;
    if (!isSnapshotShape(parsed.snapshot)) return null;
    return parsed.snapshot;
  } catch {
    return null;
  }
}

function writePersisted(uid: string, snapshot: BillingSnapshot): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(keyFor(uid), JSON.stringify({ v: 1, uid, at: Date.now(), snapshot }));
  } catch { /* quota/blocked — persistence is an optimization */ }
}

/**
 * The last verified snapshot for this uid, synchronously — memory first, then
 * the persisted copy. This is what lets a repeat visit paint the real answer
 * in its FIRST frame instead of a guess it has to take back.
 */
export function readCachedBillingSnapshot(uid: string | null | undefined): BillingSnapshot | null {
  const cleanUid = String(uid ?? '').trim();
  if (!cleanUid) return null;
  if (memory && memory.uid === cleanUid) return memory.snapshot;
  const persisted = readPersisted(cleanUid);
  if (persisted) {
    // Seed memory with at:0 so the next resolve still revalidates.
    memory = { uid: cleanUid, at: 0, snapshot: persisted };
    return persisted;
  }
  return null;
}

/** True when the memory copy is fresh enough to skip a network revalidation. */
export function isBillingSnapshotFresh(uid: string | null | undefined): boolean {
  const cleanUid = String(uid ?? '').trim();
  if (!cleanUid || !memory || memory.uid !== cleanUid) return false;
  return Date.now() - memory.at < MEMORY_TTL_MS;
}

function store(uid: string, snapshot: BillingSnapshot): BillingSnapshot {
  memory = { uid, at: Date.now(), snapshot };
  writePersisted(uid, snapshot);
  return snapshot;
}

/**
 * Drop the cached answer for this uid. Call after ANY write that can change
 * billing state (hire, seat/term change, cancel, portal return) so the next
 * paint cannot come from a copy the user just invalidated themselves.
 */
export function invalidateBillingSnapshot(uid: string | null | undefined): void {
  const cleanUid = String(uid ?? '').trim();
  if (memory && (!cleanUid || memory.uid === cleanUid)) memory = null;
  if (!cleanUid) return;
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(keyFor(cleanUid));
  } catch { /* nothing to drop */ }
}

/**
 * Wait for an id token the same way the pages always have (AUTH_WAIT_MS), but
 * as a value that can be raced against everything else rather than a serial
 * hop that everything downstream queues behind.
 */
export async function idTokenFor(auth: Auth): Promise<string | null> {
  const direct = auth.currentUser;
  if (direct) return direct.getIdToken();
  return new Promise<string | null>((resolveToken) => {
    let done = false;
    const finish = async (u: User | null) => {
      if (done) return;
      done = true;
      unsub();
      clearTimeout(timer);
      resolveToken(u ? await u.getIdToken() : null);
    };
    const unsub = auth.onAuthStateChanged((u) => { void finish(u); });
    const timer = setTimeout(() => { void finish(auth.currentUser); }, 4000);
  });
}

/**
 * Resolve this viewer's billing situation from the server.
 *
 * The parallelism is the point: the firebase surfaces, the workspace options
 * and the id token are all in flight at once, so the wall-clock cost is the
 * SLOWEST of them plus one subscription read — not their sum.
 *
 * Returns null when the answer is genuinely unknowable right now (signed out,
 * no token). Callers keep whatever they are painting rather than flipping to
 * a guess.
 */
async function resolve(uid: string): Promise<BillingSnapshot | null> {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();

  // Firebase restores its session asynchronously. In the common case (an SPA
  // navigation) it is already restored and everything below runs in parallel.
  // Cold — a direct load of /pricing or /subscription — the workspace-options
  // callable would fire unauthenticated and resolve to an empty list, which
  // reads as "personal" and is exactly the wrong answer; so wait for auth
  // FIRST in that case only, and keep the parallelism for everyone else.
  let earlyToken: string | null = null;
  if (!auth.currentUser) {
    earlyToken = await idTokenFor(auth);
    if (!earlyToken) return null;
  }
  if (auth.currentUser?.uid !== uid) return null;

  // ── Everything that does not depend on another answer, at once. ──
  // The workspace-options CALL starts the instant its module lands rather
  // than waiting on the token, and the org path's firebase surfaces warm in
  // the background, so neither shows up on the critical path.
  const optionsPromise = import('./workspaceOptionsCache')
    .then((m) => m.fetchWorkspaceOptionsCached(uid));
  const identityPromise = import('./workspaceIdentity');
  const confirmedPromise = import('./prest0nRuntime/confirmedWorkspaceCache');
  const orgModsPromise = Promise.all([import('firebase/functions'), import('firebase/app')]);
  // Nothing awaits the warm-up on the personal path; swallow its rejection so
  // it can never surface as an unhandled rejection there.
  orgModsPromise.catch(() => undefined);

  const [options, identityMod, confirmedMod, token] = await Promise.all([
    optionsPromise, identityPromise, confirmedPromise,
    earlyToken ?? idTokenFor(auth),
  ]);
  if (!token) return null;

  const lastKnownId = confirmedMod.readConfirmedWorkspace(uid)?.accountId ?? null;
  const active =
    options.find((o) => o.isActive)
    || options.find((o) => o.accountId === lastKnownId)
    || null;

  // Personal path — own workspace, unresolved, or an owner's legacy (non-co_)
  // org whose billing IS the owner's personal surface. Same rule as
  // Subscription.tsx's workspace-resolution effect.
  const isPersonal = !active
    || active.accountId === uid
    || (active.isOwner && !active.accountId.startsWith('co_'));

  if (isPersonal) {
    // An OWNED slug-keyed org names itself (2026-09-15): the owner-plane
    // endpoint applies the internal paid grant only for the workspace it is
    // asked about, and the caller's uid alone means their PERSONAL workspace.
    // Same rule as Subscription.tsx's personal fetch.
    const ownedOrgId = active && active.accountId !== uid ? active.accountId : null;
    const statusUrl = ownedOrgId
      ? `${fnUrl('getSubscriptionStatus')}?accountId=${encodeURIComponent(ownedOrgId)}`
      : fnUrl('getSubscriptionStatus');
    const resp = await fetch(statusUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = resp.ok ? await resp.json() : null;
    const sub = data?.subscription ?? null;
    const live = sub && typeof sub.plan === 'string' && sub.plan.startsWith('wu_')
      && LIVE_STATUSES.includes(String(sub.status || '')) && sub.is_trial !== true;
    if (live) {
      return {
        kind: 'plan', scope: 'personal', label: 'your workspace',
        plan: sub.plan,
        seats: typeof sub.planSeats === 'number' && sub.planSeats > 0 ? sub.planSeats : 1,
        term: termFromPlan(sub.plan),
        currentPeriodEnd: typeof sub.currentPeriodEnd === 'number' ? sub.currentPeriodEnd : null,
        isTrial: false,
        canManage: true,
        entitlementSource: typeof sub.entitlementSource === 'string' ? sub.entitlementSource : null,
      };
    }
    return {
      kind: 'offer', scope: 'personal', label: 'your workspace',
      isTrial: sub?.is_trial === true,
    };
  }

  // Org path — the created (co_) org or a non-owner's workspace. The modules
  // were warmed above, so this await is normally already settled.
  const [{ getFunctions, httpsCallable }, { getApp }] = await orgModsPromise;
  const label = identityMod.workspaceLabel(active, uid);
  const fn = httpsCallable<{ accountId: string }, any>(
    getFunctions(getApp()), 'getWorkspaceSubscriptionStatus',
  );
  try {
    const res = await fn({ accountId: active.accountId });
    const orgSub: any = res.data?.subscription ?? null;
    const canManage = res.data?.canManage === true;
    // A workspace on an internal paid grant has no Stripe subscription and
    // never will — the grant IS its live plan (2026-09-15).
    const orgSubLive = (orgSub?.hasStripeSubscription === true || orgSub?.entitlementSource === 'internal-grant')
      && LIVE_STATUSES.includes(String(orgSub?.status || ''));
    if (orgSubLive && typeof orgSub?.plan === 'string' && orgSub.plan.startsWith('wu_')) {
      return {
        kind: canManage ? 'plan' : 'view',
        scope: 'org', accountId: active.accountId, label,
        plan: orgSub.plan,
        seats: typeof orgSub.seatCount === 'number' && orgSub.seatCount > 0 ? orgSub.seatCount : 1,
        term: termFromPlan(orgSub.plan),
        currentPeriodEnd: typeof orgSub.currentPeriodEnd === 'number' ? orgSub.currentPeriodEnd : null,
        isTrial: orgSub?.isTrial === true,
        canManage,
        entitlementSource: typeof orgSub?.entitlementSource === 'string' ? orgSub.entitlementSource : null,
      };
    }
    if (canManage) {
      return {
        kind: 'offer', scope: 'org', accountId: active.accountId, label,
        isTrial: res.data?.trial?.active === true,
      };
    }
    return { kind: 'view', scope: 'org', accountId: active.accountId, label, canManage: false };
  } catch {
    // permission-denied (or any callable failure for a non-owner): the same
    // wall the subscription page shows.
    return { kind: 'wall', scope: 'org', accountId: active.accountId, label };
  }
}

/**
 * The verified answer, deduped across every consumer mounted at once and
 * written through to memory + persistence.
 *
 * Resolves to null when the answer is unknowable (signed out, no token, or
 * the resolution overran RESOLVE_TIMEOUT_MS) — never to a guess.
 */
export function resolveBillingSnapshot(
  uid: string | null | undefined,
): Promise<BillingSnapshot | null> {
  const cleanUid = String(uid ?? '').trim();
  if (!cleanUid) return Promise.resolve(null);
  if (inflight && inflight.uid === cleanUid) return inflight.promise;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const promise = Promise.race([
    resolve(cleanUid).catch(() => null),
    new Promise<null>((r) => { timer = setTimeout(() => r(null), RESOLVE_TIMEOUT_MS); }),
  ])
    .then((snapshot) => (snapshot ? store(cleanUid, snapshot) : null))
    .finally(() => {
      if (timer) clearTimeout(timer);
      if (inflight && inflight.uid === cleanUid) inflight = null;
    });

  inflight = { uid: cleanUid, promise };
  return promise;
}

/** Test seam — drops all cached state (memory only; persistence is per-uid). */
export function __resetBillingSnapshotCacheForTests(): void {
  memory = null;
  inflight = null;
}
